import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Alert, ActivityIndicator, TouchableOpacity, ScrollView } from 'react-native';
import { Text, Button } from '@app/design-system';
import { useThemeColors, useThemeAccent, useAuthTokens, configurationService, type ShopType } from '@app/core';
import { useWebsiteBuilder } from '../store/websiteBuilderStore';
import { TopToolbar } from '../components/TopToolbar/TopToolbar';
import { LeftPanel } from '../components/LeftPanel/LeftPanel';
import { CenterCanvas } from '../components/CenterCanvas/CenterCanvas';
import { RightPanel } from '../components/RightPanel/RightPanel';

interface WebsiteBuilderScreenProps {
  onBack?: () => void;
  onHome?: () => void;
  onManageShop?: () => void;
  onManageCatalog?: () => void;
  onManageUser?: () => void;
  onManageOrders?: () => void;
  onWebsiteBuilder?: () => void;
  onLogout?: () => void;
}

export const WebsiteBuilderScreen: React.FC<WebsiteBuilderScreenProps> = ({
  onBack,
  onHome,
  onManageShop,
  onManageCatalog,
  onManageUser,
  onManageOrders,
  onWebsiteBuilder,
  onLogout,
}) => {
  const themeColors = useThemeColors();
  const accentColor = useThemeAccent();
  const tokens = useAuthTokens();

  const {
    config,
    isLoading,
    loadConfig,
    loadTemplate,
    setTemplateMode,
    setSelectedShopType,
    selectedShopType,
    isTemplateMode,
    leftPanelOpen,
    rightPanelOpen,
    errors
  } = useWebsiteBuilder();

  const [shopTypes, setShopTypes] = useState<ShopType[]>([]);
  const [loadingShopTypes, setLoadingShopTypes] = useState(false);
  const [tempSelectedShopType, setTempSelectedShopType] = useState<string | null>(null);

  // Check if user is SUPER_ADMIN
  const isSuperAdmin = tokens?.userRole === 'SUPER_ADMIN';

  useEffect(() => {
    console.log('🎨 [WebsiteBuilder] Screen mounted');
    console.log('👤 [WebsiteBuilder] User role:', tokens?.userRole);
    console.log('🏪 [WebsiteBuilder] Shop ID:', tokens?.shopId);

    // Set template mode for SUPER_ADMIN
    if (isSuperAdmin) {
      console.log('👑 [WebsiteBuilder] SUPER_ADMIN detected - enabling template mode');
      setTemplateMode(true);
      loadShopTypes(); // Always load shop types for SUPER_ADMIN

      // If there's already a selected shop type in the store, sync it
      if (selectedShopType) {
        setTempSelectedShopType(selectedShopType);
      }
    } else {
      // Regular user - load shop config
      if (tokens?.shopId && tokens.shopId !== 'ALL') {
        initializeBuilder();
      }
    }

    // Auto-save interval
    const autoSaveInterval = setInterval(() => {
      const { isDirty, save, saveTemplate, isTemplateMode, selectedShopType } = useWebsiteBuilder.getState();
      if (isDirty) {
        console.log('💾 [WebsiteBuilder] Auto-saving...');
        if (isTemplateMode && selectedShopType) {
          saveTemplate(selectedShopType);
        } else {
          save();
        }
      }
    }, 30000); // 30 seconds

    return () => {
      console.log('🎨 [WebsiteBuilder] Screen unmounting');
      clearInterval(autoSaveInterval);
    };
  }, [tokens?.shopId, tokens?.userRole, isSuperAdmin]);

  // Sync temp selected shop type with store's selected shop type
  useEffect(() => {
    if (selectedShopType && !tempSelectedShopType) {
      setTempSelectedShopType(selectedShopType);
    }
  }, [selectedShopType]);

  useEffect(() => {
    if (errors.length > 0) {
      const errorMessages = errors.map(e => e.message).join('\n');
      Alert.alert('Error', errorMessages);
    }
  }, [errors]);

  const loadShopTypes = async () => {
    setLoadingShopTypes(true);
    try {
      const types = await configurationService.getShopTypes({ activeOnly: true });
      setShopTypes(types);
      console.log('✅ [WebsiteBuilder] Shop types loaded:', types);
    } catch (error) {
      console.error('❌ [WebsiteBuilder] Failed to load shop types:', error);
      Alert.alert('Error', 'Failed to load shop types');
    } finally {
      setLoadingShopTypes(false);
    }
  };

  const initializeBuilder = async () => {
    try {
      if (!tokens?.shopId || tokens.shopId === 'ALL') {
        console.error('❌ [WebsiteBuilder] Invalid shop ID');
        Alert.alert('Error', 'Shop ID not found. Please try logging in again.');
        return;
      }

      console.log('🔄 [WebsiteBuilder] Loading config for shop:', tokens.shopId);
      await loadConfig(tokens.shopId);
      console.log('✅ [WebsiteBuilder] Initialization complete');
    } catch (error) {
      console.error('❌ [WebsiteBuilder] Initialization failed:', error);
      // Don't show alert - the store will handle the error and create default config
    }
  };

  const handleShopTypeSelect = async (shopType: string) => {
    console.log('🏪 [WebsiteBuilder] Shop type selected:', shopType);
    setTempSelectedShopType(shopType);
  };

  const handleLoadTemplate = async () => {
    if (!tempSelectedShopType) {
      Alert.alert('Error', 'Please select a shop type first');
      return;
    }

    try {
      console.log('🔄 [WebsiteBuilder] Loading template for:', tempSelectedShopType);
      await loadTemplate(tempSelectedShopType);
      setSelectedShopType(tempSelectedShopType);
      console.log('✅ [WebsiteBuilder] Template loaded');
    } catch (error) {
      console.error('❌ [WebsiteBuilder] Failed to load template:', error);
      Alert.alert('Error', 'Failed to load template');
    }
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: themeColors.secondary,
    },
    builderContainer: {
      flex: 1,
      flexDirection: 'row',
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: themeColors.secondary,
    },
    loadingText: {
      marginTop: 16,
      color: themeColors.text,
    },
    templateSelectorContainer: {
      flex: 1,
      padding: 24,
      backgroundColor: themeColors.secondary,
    },
    title: {
      fontSize: 24,
      fontWeight: '700',
      color: themeColors.text,
      marginBottom: 8,
    },
    subtitle: {
      fontSize: 14,
      color: '#666',
      marginBottom: 24,
    },
    label: {
      fontSize: 14,
      fontWeight: '600',
      color: themeColors.text,
      marginBottom: 12,
    },
    shopTypeGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 12,
      marginBottom: 24,
    },
    shopTypeCard: {
      width: 150,
      padding: 16,
      borderRadius: 8,
      backgroundColor: '#FFFFFF',
      borderWidth: 2,
      borderColor: '#E5E5E5',
      alignItems: 'center',
    },
    shopTypeCardSelected: {
      borderColor: accentColor,
      backgroundColor: `${accentColor}10`,
    },
    shopTypeIcon: {
      fontSize: 32,
      marginBottom: 8,
    },
    shopTypeName: {
      fontSize: 14,
      fontWeight: '600',
      color: themeColors.text,
      textAlign: 'center',
    },
    buttonContainer: {
      flexDirection: 'row',
      gap: 12,
      marginTop: 24,
    },
    shopTypeSelectorBar: {
      backgroundColor: '#F8F9FA',
      borderBottomWidth: 1,
      borderBottomColor: '#E5E5E5',
      paddingHorizontal: 16,
      paddingVertical: 12,
      flexDirection: 'row',
      alignItems: 'center',
    },
    shopTypeChip: {
      paddingHorizontal: 16,
      paddingVertical: 8,
      borderRadius: 20,
      backgroundColor: '#FFFFFF',
      borderWidth: 1,
      borderColor: '#E5E5E5',
    },
    shopTypeChipActive: {
      backgroundColor: accentColor,
      borderColor: accentColor,
    },
    shopTypeChipText: {
      fontSize: 14,
      fontWeight: '500',
      color: themeColors.text,
    },
    shopTypeChipTextActive: {
      color: '#FFFFFF',
      fontWeight: '600',
    },
  });

  // Loading state
  if (loadingShopTypes || (isLoading && !config)) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={accentColor} />
        <Text style={styles.loadingText}>
          {loadingShopTypes ? 'Loading shop types...' : 'Loading Website Builder...'}
        </Text>
      </View>
    );
  }

  // SUPER_ADMIN - Show shop type selector if no template loaded
  if (isSuperAdmin && !config) {
    const shopTypeIcons: Record<string, string> = {
      RESTAURANT: '🍽️',
      VEGETABLE_SHOP: '🥬',
      GENERAL_STORE: '🏪',
      CHICKEN_SHOP: '🍗',
      BAKERY: '🥖',
      PHARMACY: '💊',
      ELECTRONICS: '📱',
      CLOTHING: '👕',
      CAFE: '☕',
      SUPERMARKET: '🛒',
      OTHER: '🏬'
    };

    return (
      <View style={styles.container}>
        <TopToolbar
          onBack={onBack}
          onHome={onHome}
          onManageShop={onManageShop}
          onManageCatalog={onManageCatalog}
          onManageUser={onManageUser}
          onManageOrders={onManageOrders}
          onWebsiteBuilder={onWebsiteBuilder}
          onLogout={onLogout}
        />
        <ScrollView style={styles.templateSelectorContainer}>
          <Text style={styles.title}>👑 Template Manager</Text>
          <Text style={styles.subtitle}>
            Create and manage default website templates for different shop types
          </Text>

          <Text style={styles.label}>Select Shop Type *</Text>
          <View style={styles.shopTypeGrid}>
            {shopTypes.map((type) => (
              <TouchableOpacity
                key={type.code}
                style={[
                  styles.shopTypeCard,
                  tempSelectedShopType === type.code && styles.shopTypeCardSelected
                ]}
                onPress={() => handleShopTypeSelect(type.code)}
              >
                <Text style={styles.shopTypeIcon}>
                  {shopTypeIcons[type.code] || '🏬'}
                </Text>
                <Text style={styles.shopTypeName}>{type.name}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={{
                flex: 1,
                backgroundColor: tempSelectedShopType ? accentColor : '#CCCCCC',
                paddingVertical: 12,
                borderRadius: 8,
                alignItems: 'center',
              }}
              onPress={handleLoadTemplate}
              disabled={!tempSelectedShopType}
            >
              <Text style={{ color: '#FFFFFF', fontWeight: '600', fontSize: 16 }}>
                Load Template
              </Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    );
  }

  // Regular user - Show start building screen
  if (!config) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={{ fontSize: 48, marginBottom: 16 }}>🎨</Text>
        <Text style={{ fontSize: 20, fontWeight: '700', color: themeColors.text, marginBottom: 8 }}>
          Welcome to Website Builder
        </Text>
        <Text style={{ fontSize: 14, color: '#666', textAlign: 'center', marginBottom: 24, paddingHorizontal: 32 }}>
          Create a beautiful website for your shop with drag-and-drop blocks.
          Start building now!
        </Text>
        <TouchableOpacity
          style={{
            backgroundColor: accentColor,
            paddingHorizontal: 24,
            paddingVertical: 12,
            borderRadius: 8,
          }}
          onPress={initializeBuilder}
        >
          <Text style={{ color: '#FFFFFF', fontWeight: '600', fontSize: 16 }}>
            🚀 Start Building
          </Text>
        </TouchableOpacity>
      </View>
    );
  }

  // Builder interface (for both SUPER_ADMIN and regular users)
  return (
    <View style={styles.container}>
      <TopToolbar
        onBack={onBack}
        onHome={onHome}
        onManageShop={onManageShop}
        onManageCatalog={onManageCatalog}
        onManageUser={onManageUser}
        onManageOrders={onManageOrders}
        onWebsiteBuilder={onWebsiteBuilder}
        onLogout={onLogout}
      />

      {/* Shop Type Selector for SUPER_ADMIN */}
      {isSuperAdmin && (
        <View style={styles.shopTypeSelectorBar}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, flex: 1 }}>
            <Text style={{ fontSize: 14, fontWeight: '600', color: themeColors.text }}>
              Shop Type:
            </Text>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{ flex: 1 }}>
              <View style={{ flexDirection: 'row', gap: 8 }}>
                {shopTypes.map((type) => (
                  <TouchableOpacity
                    key={type.code}
                    style={[
                      styles.shopTypeChip,
                      selectedShopType === type.code && styles.shopTypeChipActive
                    ]}
                    onPress={() => handleShopTypeSelect(type.code)}
                  >
                    <Text
                      style={[
                        styles.shopTypeChipText,
                        selectedShopType === type.code && styles.shopTypeChipTextActive
                      ]}
                    >
                      {type.name}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>
            {tempSelectedShopType && tempSelectedShopType !== selectedShopType && (
              <TouchableOpacity
                style={{
                  backgroundColor: accentColor,
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 6,
                }}
                onPress={handleLoadTemplate}
              >
                <Text style={{ color: '#FFFFFF', fontWeight: '600', fontSize: 14 }}>
                  Load Template
                </Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
      )}

      <View style={styles.builderContainer}>
        {leftPanelOpen && <LeftPanel />}

        <CenterCanvas />

        {rightPanelOpen && <RightPanel />}
      </View>
    </View>
  );
};

